import type { FeatureContent } from './types';

export const salaryNegotiation: FeatureContent = {
  slug: 'salary-negotiation',
  path: '/features/salary-negotiation',
  audience: 'candidate',
  icon: 'HandCoins',
  name: 'Salary Negotiation',
  navLabel: 'Salary Negotiation',
  navDescription: 'Negotiate from knowledge, not nerves.',
  primaryKeyword: 'salary negotiation help',
  seo: {
    title: 'Salary Negotiation Help & Coaching | Talenture.ai',
    description:
      'Salary negotiation help that turns an offer into a confident, evidence-based counter. Benchmark against market percentiles, get a script, and spot red flags.',
    keywords: [
      'salary negotiation help',
      'how to negotiate a job offer',
      'market rate salary',
      'salary negotiation coach',
      'offer negotiation',
      'counter offer script',
    ],
    ogTitle: 'Negotiate From Knowledge, Not Nerves | Talenture.ai',
    ogDescription:
      'Turn an offer into a confident, evidence-based counter. Benchmark against market percentiles, get a script, and spot red flags before you sign.',
  },
  hero: {
    eyebrow: 'For candidates',
    h1: 'Negotiate from knowledge, not nerves',
    subhead:
      'Most people accept the first number because they do not know what to ask for. Talenture benchmarks your offer against the market and hands you a clear, evidence-based script, so you counter with confidence.',
    primaryCta: { label: 'Create free account', href: '/candidate-auth' },
    secondaryCta: { label: 'Check your CV free', href: '/cv-vs-market' },
  },
  definition: {
    heading: 'What is salary negotiation help?',
    body: 'Salary negotiation help is guidance that turns a job offer into a confident counter. Talenture evaluates the whole package, benchmarks your pay against market percentiles for your role and location, flags any red flags in the contract, and gives you talking points and a script, so you negotiate from evidence rather than guesswork.',
  },
  sections: [
    {
      icon: 'Scale',
      title: 'The whole offer, evaluated',
      body: 'Salary is only part of an offer. Talenture looks at the full package and produces talking points tuned to your role and seniority.',
      bullets: [
        'Base, bonus, equity, and PTO weighed together',
        'Actionable scripts and talking points',
        'Tuned to your role and level',
      ],
    },
    {
      icon: 'BarChart3',
      title: 'Benchmarked against the market',
      body: 'You see where your offer sits against real ranges, so you know whether to push and by how much.',
      bullets: [
        'Percentile ranges: 25th, median, 75th',
        'Cost-of-living context',
        'EU versus US norms',
      ],
    },
    {
      icon: 'AlertTriangle',
      title: 'Red flags, before you sign',
      body: 'Some terms cost you later. Talenture flags restrictive non-competes, unusual probation periods, and other clauses worth questioning before you commit.',
    },
  ],
  proofPoints: [
    { value: '25th / 50th / 75th', label: 'market percentile benchmarks' },
    { value: 'Full package', label: 'base, bonus, equity, and PTO' },
    { value: 'Always free', label: 'for candidates' },
  ],
  howTo: {
    heading: 'How to negotiate a job offer with Talenture',
    intro: 'Four steps from offer to confident counter.',
    steps: [
      {
        title: 'Share the offer',
        body: 'Enter the role, location, and the details of the offer: base, bonus, equity, and any benefits on the table.',
      },
      {
        title: 'Benchmark it',
        body: 'Talenture compares your offer against market percentiles for your role and location, with cost-of-living and regional context, so you know where it really sits.',
      },
      {
        title: 'Check the fine print',
        body: 'Talenture flags red flags such as restrictive non-competes or unusual probation terms, so nothing surprises you after you sign.',
      },
      {
        title: 'Counter with a script',
        body: 'Use the talking points and ready-made script, tuned to your role and seniority, to make an evidence-based counter with confidence.',
      },
    ],
  },
  faq: [
    {
      question: 'How do I know if my offer is fair?',
      answer:
        'Talenture benchmarks your offer against market percentile ranges for your role and location, the 25th, median, and 75th, with cost-of-living and regional context. You see exactly where your offer sits, so you know whether and how hard to push.',
    },
    {
      question: 'What does Talenture actually give me to negotiate with?',
      answer:
        'A set of talking points and a ready-made script tuned to your role and seniority, built from the full package of base, bonus, equity, and PTO. You walk in with concrete language, not just a feeling that you should ask for more.',
    },
    {
      question: 'Can it spot problems in the contract?',
      answer:
        'Yes. Talenture flags red flags such as restrictive non-competes and unusual probation terms, so you can question them before signing rather than discovering them later.',
    },
    {
      question: 'Is the salary negotiation help free?',
      answer:
        'Yes. Talenture is always free for candidates, with no credit card. The negotiation coaching and benchmarking are part of the free career tools.',
    },
  ],
  relatedSlugs: ['upskilling-recommendations', 'match-insights', 'application-tracking'],
};
